import logger from '../debug';
import { NavigationEvent } from './types';
import { getFileProperties } from './url-helper';

export type AnchorData = { type: NavigationEvent; eventData: { url: string; label?: string | null }; anchor: HTMLAnchorElement };
export type MdatData = { name?: string; anchorData?: AnchorData; attributeData: Record<string, unknown> };
const attributeNamespace = 'mdat';

const mergeData = (data: Record<string, unknown>, path: string[], value: string | undefined): Record<string, unknown> => {
  if (path.length === 1) {
    return { [path[0]]: value, ...data };
  }

  const field = data[path[0]] || {};
  if (typeof field !== 'object') {
    logger.warn(`mdat cannot merge attribute ${path.join('.')}`);
    return data;
  }

  return { ...data, [path[0]]: mergeData(field as Record<string, unknown>, path.slice(1), value) };
};

const toPath = (name: string) => name.split('_').map(prop => prop.substring(0, 1).toLowerCase() + prop.substring(1));
const camelize = (value?: string) => value?.replace(/-[a-zA-Z]/g, x => x[1].toUpperCase());

const getAnchorData = (anchor: HTMLAnchorElement, getNavigationEventType: (url: URL) => NavigationEvent) => {
  try {
    const url = new URL(anchor.href);
    if (!url.protocol.startsWith('http')) return undefined;

    return {
      type: getNavigationEventType(url),
      eventData: {
        url: anchor.href,
        label: anchor.innerText || anchor.textContent,
        ...getFileProperties(url),
      },
      anchor,
    };
  } catch {
    return undefined;
  }
};

export const getEventData = (element: HTMLElement, getNavigationEventType?: (url: URL) => NavigationEvent): MdatData => {
  let attributeData: MdatData['attributeData'] = {};
  let name: string | undefined = undefined;
  let anchor: HTMLAnchorElement | undefined = undefined;
  let currentElement: HTMLElement | null = element;

  while (currentElement) {
    const mdatAttributes = Object.entries(currentElement.dataset || {}).filter(([key]) => key.startsWith(attributeNamespace));
    mdatAttributes.forEach(([key, value]) => {
      if (key === attributeNamespace) {
        name = name || camelize(value);
      } else {
        const name = key.substring(attributeNamespace.length);
        attributeData = mergeData(attributeData, toPath(name), value);
      }
    });

    if (!anchor && currentElement instanceof HTMLAnchorElement) {
      anchor = currentElement;
    }

    currentElement = currentElement.parentElement;
  }

  return {
    name,
    attributeData,
    anchorData: anchor && getNavigationEventType && getAnchorData(anchor, getNavigationEventType),
  };
};

/**
 * Gets the mdat attribute data for a given element
 * @param elementId the element or the id of the element for which to get the data
 * @returns the mdat attribute data
 */
export const getAttributeData = (elementId: string | HTMLElement): Record<string, unknown> => {
  const element = typeof elementId === 'object' ? elementId : document.getElementById(elementId);

  if (!element) return {};

  const { attributeData } = getEventData(element);
  return attributeData;
};
